import React from 'react';
import PropTypes from 'prop-types';
import { sportTypeIds } from '../../../../../utils/eventConstants';
import breadCrumbData from '../../../../SeoLocationLandingPage/breadCrumb.json';
import config from '../../../../../../config';

const SeoURL = ({ event }) => {
    const baseUrls = {
        tennis: {
            eventsURL: '/tennis-tournaments',
            leaguesURL: '/tennis-leagues',
            rankings: {
                boys: '/boys-tennis-rankings',
                girls: '/girls-tennis-rankings',
                mens: '/mens-tennis-rankings',
                womens: '/womens-tennis-rankings',
            },
        },
        pickleball: {
            eventsURL: '/pickleball-tournaments',
            leaguesURL: '/pickleball-leagues',
        },
    };

    const location = event?.eventLocations?.[0] || {};
    const isTennisEvent = event?.sportTypeId === sportTypeIds.TENNIS;
    const isEventInUnitedStates = (location.countryName === 'United States' || location.countryCode3 === 'USA' || location.countryIoc === 'USA' || location.countryCode2 === 'US');

    const locationHelper = () => {
        let countryURL = '';
        let stateURL = '';
        let cityURL = '';
        let formattedCountryName = '';
        let formattedStateName = '';
        let formattedCityName = '';

        if (location) {
            const { countryName, stateName, cityName } = location;
            const formatString = (str) => str.toLowerCase().replace(/\s+/g, '-');

            if (countryName) {
                formattedCountryName = formatString(countryName);
                if (breadCrumbData[formattedCountryName]) {
                    countryURL = `/${formattedCountryName}`;
                }
            }

            if (countryURL && stateName) {
                formattedStateName = formatString(stateName);
                if (breadCrumbData[formattedCountryName][formattedStateName]) {
                    stateURL = `${countryURL}/${formattedStateName}`;
                }
            }

            if (stateURL && cityName) {
                formattedCityName = formatString(cityName);
                if (breadCrumbData[formattedCountryName][formattedStateName][formattedCityName]) {
                    cityURL = `${stateURL}/${formattedCityName}`;
                }
            }
        }

        return {
            countryURL,
            stateURL,
            cityURL,
        };
    };

    const locationHelperData = locationHelper();

    const generateURLs = (baseURLs, locationURL, locationName, sportName, isTennis) => {
        const urls = [
            {
                url: `${config.nextAppHost}/locations${locationURL}`,
                title: `${locationName} ${sportName} Homepage`,
            },
            {
                url: `${config.nextAppHost}/rankings${baseURLs.leaguesURL}${locationURL}`,
                title: `${locationName} ${sportName} Leagues`,
            },
            {
                url: `${config.nextAppHost}/rankings${baseURLs.eventsURL}${locationURL}`,
                title: `${locationName} ${sportName} Tournaments`,
            },
        ];

        if (isTennis) {
            urls.push(
                {
                    url: `${config.nextAppHost}/rankings${baseURLs.rankings.boys}${locationURL}`,
                    title: `${locationName} Boys' ${sportName} Rankings`,
                },
                {
                    url: `${config.nextAppHost}/rankings${baseURLs.rankings.girls}${locationURL}`,
                    title: `${locationName} Girls' ${sportName} Rankings`,
                },
                {
                    url: `${config.nextAppHost}/rankings${baseURLs.rankings.mens}${locationURL}`,
                    title: `${locationName} Men's ${sportName} Rankings`,
                },
                {
                    url: `${config.nextAppHost}/rankings${baseURLs.rankings.womens}${locationURL}`,
                    title: `${locationName} Women's ${sportName} Rankings`,
                },
            );
        }

        return urls;
    };

    const sportName = isTennisEvent ? 'Tennis' : 'Pickleball';
    const sportBaseURLs = isTennisEvent ? baseUrls.tennis : baseUrls.pickleball;
    const countryURLs = locationHelperData.countryURL ? generateURLs(sportBaseURLs, locationHelperData.countryURL, location.countryName, sportName, isTennisEvent) : [];
    const stateURLs = locationHelperData.stateURL ? generateURLs(sportBaseURLs, locationHelperData.stateURL, location.stateName, sportName, isTennisEvent) : [];
    const cityURLs = locationHelperData.cityURL ? generateURLs(sportBaseURLs, locationHelperData.cityURL, location.cityName, sportName, isTennisEvent) : [];

    const urls = isEventInUnitedStates ? [...stateURLs, ...cityURLs] : countryURLs;

    if (urls.length === 0 || !location.countryName) {
        return null;
    }

    return (
        <div className="utr-card">
            <div className="mt14 p8">
                <ul>
                    {isEventInUnitedStates && location.cityName && cityURLs.length > 0 && (
                        <div className="p8">
                            <div className="fs-18 text-bold">{location.cityName}</div>
                            {cityURLs.map((urlObj, idx) => (
                                <li className="mt-2" key={urlObj.title}>
                                    <a href={urlObj.url} data-testid={`event-profile.seo-url.city-ranking-${idx}`}>{urlObj.title}</a>
                                </li>
                            ))}
                        </div>
                    )}

                    {isEventInUnitedStates && location.stateName && stateURLs.length > 0 && (
                        <div className="p8">
                            <div className="fs-18 text-bold">{location.stateName}</div>
                            {stateURLs.map((urlObj, idx) => (
                                <li className="mt-2" key={urlObj.title}>
                                    <a href={urlObj.url} data-testid={`event-profile.seo-url.state-ranking-${idx}`}>{urlObj.title}</a>
                                </li>
                            ))}
                        </div>
                    )}

                    {!isEventInUnitedStates && countryURLs.length > 0 && (
                        <div className="p8">
                            <div className="fs-18 text-bold">{location.countryName}</div>
                            {countryURLs.map((urlObj, idx) => (
                                <li className="mt-2" key={urlObj.title}>
                                    <a href={urlObj.url} data-testid={`event-profile.seo-url.country-ranking-${idx}`}>{urlObj.title}</a>
                                </li>
                            ))}
                        </div>
                    )}
                </ul>
            </div>
        </div>
    );
};

SeoURL.propTypes = {
    event: PropTypes.shape({
        sportTypeId: PropTypes.number.isRequired,
        eventLocations: PropTypes.arrayOf(PropTypes.shape({
            countryName: PropTypes.string.isRequired,
            stateName: PropTypes.string,
            cityName: PropTypes.string,
            countryCode2: PropTypes.string,
            countryCode3: PropTypes.string,
            countryIoc: PropTypes.string,
        })).isRequired,
    }).isRequired,
};

export default SeoURL;
